import { addBreadcrumb } from '@sentry/react';
import { Amplify } from 'aws-amplify';
import type { CredentialsAndIdentityIdProvider } from '@aws-amplify/core';
import {
  cognitoCredentialsProvider,
  cognitoUserPoolsTokenProvider,
} from 'aws-amplify/auth/cognito';
import {
  initAuth,
  type AuthLibraryOptions,
  type AuthResourcesConfig,
} from '@hssoft/auth-web-shared-lib';

import { isBrowserNetworkFailure } from '../helpers/networkError/networkError.helper';

/**
 * Wrap Amplify's Cognito credentials provider so a network failure against the
 * IDENTITY pool cannot break a session read (WA-3800 / Sentry WAWIASSIST-246).
 *
 * Why this exists: because an `identityPoolId` is configured (we need it only so
 * S3 uploads can resolve `private/{identityId}/…` — see `files.helper.ts`),
 * Amplify's `fetchAuthSession()` always calls Cognito Identity `GetId` /
 * `GetCredentialsForIdentity` *after* it has already read the user-pool tokens
 * (`@aws-amplify/core`'s `AuthClass.fetchAuthSession`). If that leg throws, the
 * whole call rejects — so a valid, in-hand ID token is thrown away and every
 * caller fails: `customerGUID()`, `idToken()`, and Amplify's own internals
 * (`fetchUserAttributes()` reads the session the same way). WA-3800 is that
 * rejection reaching an RxJS subscriber as an unhandled error.
 *
 * A browser-level network failure here is transient and not per-occurrence
 * actionable, so it resolves to `undefined` instead: `fetchAuthSession()` then
 * returns `{ tokens, credentials: undefined, identityId: undefined }` — the
 * shape its callers already handle via optional chaining. Nothing is cached, so
 * the next call retries.
 *
 * Deliberately narrow: anything that is NOT a network failure (a
 * misconfigured pool, `NotAuthorizedException`, an expired login) still throws,
 * because those are real and must stay visible. S3 flows are unaffected in
 * substance — an upload attempted with no identityId fails Amplify's own
 * `NoIdentityId` validation instead of with a `NetworkError`, and an upload
 * while offline could not have succeeded either way.
 */
export const createNetworkTolerantCredentialsProvider = (
  inner: CredentialsAndIdentityIdProvider = cognitoCredentialsProvider,
): CredentialsAndIdentityIdProvider => ({
  async getCredentialsAndIdentityId(getCredentialsOptions) {
    try {
      return await inner.getCredentialsAndIdentityId(getCredentialsOptions);
    } catch (error) {
      if (!isBrowserNetworkFailure(error)) {
        throw error;
      }
      // Not reported as an event: it is expected offline behaviour. The
      // breadcrumb keeps it visible in whatever error IS worth reporting later
      // in the same session (e.g. a failed upload), so triage can tell "the
      // identity pool was unreachable" from "the identity pool is misconfigured".
      addBreadcrumb({
        category: 'auth',
        level: 'warning',
        message: 'Cognito identity pool unreachable — continuing without AWS credentials',
        data: { error: (error as { name?: string })?.name ?? 'unknown' },
      });

      return undefined;
    }
  },
  clearCredentialsAndIdentityId() {
    inner.clearCredentialsAndIdentityId();
  },
});

/**
 * Configure Amplify for the app: the shared auth library's `initAuth`, plus our
 * network-tolerant credentials provider.
 *
 * The `setAuthConfig` call below is REQUIRED, not defensive. `initAuth` calls
 * `Amplify.configure` from the `aws-amplify` package, whose wrapper only injects
 * the default providers — and only calls
 * `cognitoUserPoolsTokenProvider.setAuthConfig(...)` — when NO
 * `libraryOptions.Auth` is supplied ("If Auth options are provided, always just
 * configure as is", `aws-amplify/dist/esm/initSingleton.mjs`). Supplying our own
 * provider takes that branch away, and without the user-pool config the token
 * orchestrator reports itself unconfigured and `getTokens()` returns `null` for
 * every caller — i.e. nobody can authenticate. The config is read back from the
 * singleton so it is exactly the parsed, frozen object Amplify itself holds.
 */
export const initAppAuth = (resources: AuthResourcesConfig, options?: AuthLibraryOptions): void => {
  initAuth(resources, {
    ...options,
    Auth: {
      tokenProvider: cognitoUserPoolsTokenProvider,
      credentialsProvider: createNetworkTolerantCredentialsProvider(),
    },
  });

  const authConfig = Amplify.getConfig().Auth;
  if (authConfig) {
    cognitoUserPoolsTokenProvider.setAuthConfig(authConfig);
  }
};
